import { FC } from "react";
import cn from "classnames";
import { useDropdownContext } from "../dropdown-context";
import { Item } from "../dropdown";
import { Variant } from "../enum";

export interface DropdownItemProps {
  item: Item;
  index: number;
}

const DropdownItem: FC<DropdownItemProps> = ({
  item,
  index,
  children,
  ...rest
}) => {
  const { disabled, isDivider, isSelectable } = item;
  const {
    getItemProps,
    getDividerProps,
    getSelectableDividerProps,
    highlightedIndex,
    getBlocksWith,
    selectedItem,
    variant,
  } = useDropdownContext();

  if (!isDivider && !getItemProps) {
    throw new Error(
      "Using Dropdown.Item component as a 'list item' requires getItemProps to be defined",
    );
  }

  if (isDivider && !getDividerProps) {
    throw new Error(
      "Using the Dropdown.Item component as a 'divider' requires getDividerProps to be defined",
    );
  }

  const renderDivider = () => (
    <li
      {...getDividerProps({
        item,
        index,
        className: cn(
          !item.label && getBlocksWith("__divider--line"),
          index === highlightedIndex && getBlocksWith("__divider--highlighted"),
        ),
        "aria-label": item.value,
        ...rest,
      })}
    >
      {children}
    </li>
  );

  const renderItem = () => (
    <li
      {...getItemProps({
        item,
        index,
        className: cn(
          index === highlightedIndex && getBlocksWith("__item--highlighted"),
          item === selectedItem && getBlocksWith("__item--selected"),
          item.isSelected && getBlocksWith("__item--multiselected"),
        ),
        disabled,
        "aria-selected":
          variant === Variant.LISTBOX
            ? Boolean(item === selectedItem || item.isSelected)
            : undefined,
        ...rest,
      })}
    >
      {children}
    </li>
  );

  const renderSelectableDivider = () => (
    <li
      {...getSelectableDividerProps({
        item,
        index,
        className: cn(
          !item.label && getBlocksWith("__divider--line"),
          index === highlightedIndex &&
            getBlocksWith("__selectable_divider--highlighted"),
          item === selectedItem &&
            getBlocksWith("__selectable_divider--selected"),
          item.isSelected &&
            getBlocksWith("__selectable_divider--multiselected"),
        ),
        disabled,
        "aria-selected":
          variant === Variant.LISTBOX
            ? Boolean(item === selectedItem || item.isSelected)
            : undefined,
        ...rest,
      })}
    >
      {children}
    </li>
  );

  const getItem = () => {
    if (isSelectable && isDivider) {
      return renderSelectableDivider();
    } else {
      return isDivider ? renderDivider() : renderItem();
    }
  };

  return getItem();
};

export default DropdownItem;
